import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENodeType } from '@hubertai/screening-question-types';
import type { TAnyClientQuestionType, TNode } from '@hubertai/screening-question-types';
import { HubertButton } from 'components/HubertUI/HubertButton';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import SearchQuestion from 'components/Questionsv2/SearchQuestion';
import type { TRuleSource } from 'components/Questionsv2/SearchQuestion/ruleTargetReach';
import {
  appendActionTargets,
  moveActionTarget,
  removeActionTarget,
  targetId,
} from 'components/Rules/helpers/ruleUpdates';
import { LanguageExpandableText } from 'components/Rules/LanguageExpandableText';
import RuleActionJumpToList from 'components/Rules/RuleActionJumpToList';
import { upsertQuestionRule } from 'rtk/Question.Editor.Slice';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionIsReadonly } from 'rtk/questionEditorSelector';
import type { TQuestionRule, TQuestionRuleAction } from 'types/rules.types';

interface Props {
  rule: TQuestionRule;
  action: TQuestionRuleAction;
  actionIdx: number;
}

const RuleActionJumpTo = ({ rule, action, actionIdx }: Props) => {
  const { t } = useTranslation('Templates');
  const { questionId } = useQuestionEditorContext();
  const dispatch = useAppDispatch();
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const question = useAppSelector((state) => state.questionEditor.byId[questionId]);
  const ruleSource: TRuleSource = {
    kind: 'question',
    id: question.id,
    visibility: question.visibility,
    clientId: question.client_id,
    accessGroupIds: question.access_groups_ids,
  };
  const [showPicker, setShowPicker] = useState(false);

  const commitRule = (nextRule: TQuestionRule) => dispatch(upsertQuestionRule({ questionId, rule: nextRule }));

  return (
    <div className="mt-3 flex flex-col gap-3">
      <RuleActionJumpToList
        targetIds={action.value.map(targetId)}
        isReadOnly={isReadOnly}
        renderLanguages={(questionConfig) => (
          <LanguageExpandableText
            availableLanguages={questionConfig.available_languages}
            renderText={(language) => (
              <div dangerouslySetInnerHTML={{ __html: questionConfig.text[language]?.join(' ') ?? '' }} />
            )}
          />
        )}
        onMoveUp={(idx) => commitRule(moveActionTarget(rule, actionIdx, idx, idx - 1))}
        onMoveDown={(idx) => commitRule(moveActionTarget(rule, actionIdx, idx, idx + 1))}
        onRemove={(idx) => commitRule(removeActionTarget(rule, actionIdx, idx))}
      />

      {!isReadOnly ? (
        <>
          <HubertButton size="sm" variant="outline" onClick={() => setShowPicker((v) => !v)}>
            {t('createActionButtons.findQuestion')}
          </HubertButton>

          {showPicker ? (
            <div className="my-4 h-[80vh] w-full rounded-md border-2 border-solid bg-white p-6">
              <SearchQuestion
                ruleSource={ruleSource}
                onAddQuestion={{
                  show: true,
                  click: (question: TAnyClientQuestionType) => {
                    const newNode: TNode = { type: ENodeType.Question, id: question.id, version: question.version };
                    commitRule(appendActionTargets(rule, actionIdx, [newNode]));
                    setShowPicker(false);
                  },
                }}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default RuleActionJumpTo;
